import { Table, TableHead, TableBody, Paper } from '@material-ui/core'
import { t } from 'i18next'
import React from 'react'
import { CalculatedVersion } from '../../../calculators/use-calculator'
import { EmissionModel } from '../../../emission-coefficients/emission-coefficients'
import { EmissionType } from '../../../generated/graphql'
import { purchasedEnergySubCategories } from '../../../provider/result-categories'
import {
  largeEmissionPerYearUnit,
  formatLargeEmissionPerYear,
  formatLargeEmission,
  emissionPerAreaUnit,
  formatEmissionPerArea,
  largeEmissionUnit,
} from '../../../util/formatters'
import { ReportHeader } from '../../common/ReportHeader'
import { ResultsTableHeaderRow } from '../../common/ResultsTableHeaderRow'
import { ResultsTableRow } from '../../common/ResultsTableRow'
import { Section } from '../../common/Section'
import { PageBreak } from '../../common/PageBreak'
import { useReportStyles } from '../use-report-styles'
import { useCarbonFootprintResults } from './use-carbon-footprint-results'

type Props = {
  versions: CalculatedVersion[]
  emissionModel: EmissionModel
  projectName: string
}

export default function TotalCarbonFootprintReportTable({
  versions,
  emissionModel,
  projectName,
}: Props): React.ReactElement {
  const classes = useReportStyles()
  const {
    getFirstYearResults,
    getCumulativeResults,
    getInherentCumulativeResults,
    getLifetimeFootprints,
  } = useCarbonFootprintResults(versions, emissionModel)

  return (
    <>
      <Section>
        <Paper className={classes.paper} elevation={3}>
          <Table>
            <TableHead>
              <ResultsTableHeaderRow
                title={t('carbonFootprint.table.lifetimeFootprint')}
                values={versions.map((version) => version.name)}
              />
            </TableHead>
            <TableBody>
              {Object.values(EmissionType).map((type) => (
                <ResultsTableRow
                  key={type}
                  title={`${t(
                    `isoCategoryBy.emissionType.${type}`,
                  )}, ${largeEmissionUnit}`}
                  values={getLifetimeFootprints(type)}
                  formatter={formatLargeEmission}
                />
              ))}
              <ResultsTableRow
                title={`${t('common.total')}, ${largeEmissionUnit}`}
                values={getLifetimeFootprints()}
                formatter={formatLargeEmission}
                highlight
              />
            </TableBody>
          </Table>
        </Paper>
      </Section>

      <PageBreak />

      <ReportHeader
        title={projectName}
        subtitle1={t('carbonFootprint.title')}
        subtitle2={t('carbonFootprint.tabs.results')}
      />
      <Section>
        <Paper className={classes.paper} elevation={3}>
          <Table>
            <TableHead>
              <ResultsTableHeaderRow
                title={t('carbonFootprint.table.firstYearEmissions')}
                values={versions.map((version) => version.name)}
              />
            </TableHead>
            <TableBody>
              {purchasedEnergySubCategories.map((subCategory) => (
                <ResultsTableRow
                  key={subCategory.nameKey}
                  title={`${t(
                    subCategory.nameKey,
                  )}, ${largeEmissionPerYearUnit}`}
                  values={getFirstYearResults([subCategory])}
                  formatter={formatLargeEmissionPerYear}
                  hideIfValuesIrrelevant
                />
              ))}
              <ResultsTableRow
                title={`${t('common.total')}, ${largeEmissionPerYearUnit}`}
                values={getFirstYearResults(purchasedEnergySubCategories)}
                formatter={formatLargeEmissionPerYear}
                highlight
              />
            </TableBody>

            <TableHead>
              <ResultsTableHeaderRow
                title={t('carbonFootprint.table.inspectionPeriodEmissions')}
                values={versions.map((version) => version.name)}
              />
            </TableHead>
            <TableBody>
              {purchasedEnergySubCategories.map((subCategory) => (
                <ResultsTableRow
                  key={subCategory.nameKey}
                  title={`${t(
                    subCategory.nameKey,
                  )}, ${largeEmissionPerYearUnit}`}
                  values={getCumulativeResults([subCategory])}
                  formatter={formatLargeEmission}
                  hideIfValuesIrrelevant
                />
              ))}
              <ResultsTableRow
                title={`${t('common.total')}, ${largeEmissionPerYearUnit}`}
                values={getCumulativeResults(purchasedEnergySubCategories)}
                formatter={formatLargeEmission}
                highlight
              />
            </TableBody>

            <TableHead>
              <ResultsTableHeaderRow
                title={t('carbonFootprint.table.inherentEmissions')}
                values={versions.map((version) => version.name)}
              />
            </TableHead>
            <TableBody>
              {purchasedEnergySubCategories.map((subCategory) => (
                <ResultsTableRow
                  key={subCategory.nameKey}
                  title={`${t(subCategory.nameKey)}, ${emissionPerAreaUnit}`}
                  values={getInherentCumulativeResults([subCategory])}
                  formatter={formatEmissionPerArea}
                  hideIfValuesIrrelevant
                />
              ))}
              <ResultsTableRow
                title={`${t('common.total')}, ${emissionPerAreaUnit}`}
                values={getInherentCumulativeResults(
                  purchasedEnergySubCategories,
                )}
                formatter={formatEmissionPerArea}
                highlight
              />
            </TableBody>
          </Table>
        </Paper>
      </Section>
    </>
  )
}
